import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  CHANGELOG_KIND_LABELS,
  CHANGELOG_KINDS,
  PUBLISH_STATUS_LABELS,
  renderHelpMarkdown,
  type ChangelogEntryAdmin,
  type ChangelogEntryBody,
  type ChangelogKind,
} from "@klantpunt/shared";
import { IconPlus, IconTrash } from "../app/icons";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { changelogAdminKey, useChangelogAdmin, useKbArticles } from "../features/helpcenter/api";
import { errMsg } from "../features/support/api";
import "../features/helpcenter/helpcenter.css";
import { SupportGuard } from "./SupportInbox";

/** Changelog beheren (docs/HELPCENTER.md §6). Besluit 3: leeg tot de MVP af is. */
export function ChangelogAdminPage() {
  return (
    <SupportGuard>
      <ChangelogAdmin />
    </SupportGuard>
  );
}

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
const blank = (): ChangelogEntryBody => ({
  date: today(),
  kind: "new",
  module: null,
  title: "",
  body: "",
  articleId: null,
  showInApp: true,
});

function ChangelogAdmin() {
  const q = useChangelogAdmin();
  const [editing, setEditing] = useState<ChangelogEntryAdmin | "new" | null>(null);
  const rows = q.data ?? [];
  return (
    <div className="page">
      <p className="label">
        <Link to="/platform">Platformbeheer</Link> · <Link to="/platform/helpcenter">Helpcenter</Link>
      </p>
      <div className="titlerow">
        <h1 className="dot">Updates</h1>
        <button type="button" className="btn primary" onClick={() => setEditing("new")}>
          <IconPlus size={20} aria-hidden="true" />
          Nieuwe update
        </button>
      </div>
      <p className="note">
        Afspraak: we publiceren pas updates als de MVP af is. Tot die tijd blijft "Nieuw in Klantpunt" leeg en verborgen
        op de helpsite.
      </p>
      {editing && (
        <EntryEditor
          key={editing === "new" ? "new" : editing.id}
          entry={editing === "new" ? null : editing}
          onDone={() => setEditing(null)}
        />
      )}
      {q.isPending && <Loading />}
      {q.isSuccess && rows.length === 0 && <p className="muted">Nog geen updates.</p>}
      <ul className="kb-admin-rows">
        {rows.map((e) => (
          <li key={e.id}>
            <button type="button" className="kb-admin-row" onClick={() => setEditing(e)}>
              <span className="muted small">{e.date}</span>
              <span className="chip">{CHANGELOG_KIND_LABELS[e.kind]}</span>
              <span className="grow">{e.title}</span>
              <span className={`chip${e.status === "published" ? " sp-st-open" : ""}`}>
                {PUBLISH_STATUS_LABELS[e.status]}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EntryEditor({ entry, onDone }: { entry: ChangelogEntryAdmin | null; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const articles = useKbArticles();
  const [form, setForm] = useState<ChangelogEntryBody>(() =>
    entry
      ? {
          date: entry.date,
          kind: entry.kind,
          module: entry.module,
          title: entry.title,
          body: entry.body,
          articleId: entry.articleId,
          showInApp: entry.showInApp,
        }
      : blank(),
  );
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof ChangelogEntryBody>(k: K, v: ChangelogEntryBody[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function run(fn: () => Promise<unknown>, done: string, close = true) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: changelogAdminKey });
      toast(done);
      if (close) onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    entry
      ? api<ChangelogEntryAdmin>(`/helpcenter/changelog/${entry.id}`, { method: "PUT", body: form })
      : api<ChangelogEntryAdmin>("/helpcenter/changelog", { method: "POST", body: form });

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(save, "Update opgeslagen");
  }
  const publish = () =>
    run(async () => {
      const saved = await save();
      await api(`/helpcenter/changelog/${saved.id}/publish`, { method: "POST", body: { notify } });
    }, "Update gepubliceerd");

  return (
    <form className="panel" onSubmit={submit} aria-labelledby="upd-h">
      <div className="titlerow">
        <h2 id="upd-h">{entry ? "Update bewerken" : "Nieuwe update"}</h2>
        {entry && <span className="chip">{PUBLISH_STATUS_LABELS[entry.status]}</span>}
      </div>
      <div className="kb-fields">
        <label className="field">
          <span>Datum</span>
          <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} required />
        </label>
        <label className="field">
          <span>Soort</span>
          <select value={form.kind} onChange={(e) => set("kind", e.target.value as ChangelogKind)}>
            {CHANGELOG_KINDS.map((k) => (
              <option key={k} value={k}>
                {CHANGELOG_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Module (optioneel)</span>
          <input
            value={form.module ?? ""}
            maxLength={60}
            placeholder="Bijvoorbeeld Assets"
            onChange={(e) => set("module", e.target.value.trim() ? e.target.value : null)}
          />
        </label>
      </div>
      <label className="field">
        <span>Titel</span>
        <input value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={160} />
      </label>
      <div className="kb-editor kb-upd">
        <label className="field">
          <span>Tekst (Markdown)</span>
          <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={6} maxLength={20000} />
        </label>
        <div className="kb-preview kb-article" aria-label="Voorbeeld" data-testid="upd-preview">
          <div dangerouslySetInnerHTML={{ __html: renderHelpMarkdown(form.body) }} />
        </div>
      </div>
      <label className="field">
        <span>Artikel (optioneel)</span>
        <select value={form.articleId ?? ""} onChange={(e) => set("articleId", e.target.value || null)}>
          <option value="">Geen</option>
          {(articles.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.title}
            </option>
          ))}
        </select>
      </label>
      <label className="check">
        <input type="checkbox" checked={form.showInApp} onChange={(e) => set("showInApp", e.target.checked)} />
        Ook tonen in de app (met een stip bij Hulp)
      </label>
      {entry?.status !== "published" && (
        <label className="check">
          <input
            type="checkbox"
            checked={notify}
            disabled={Boolean(entry?.emailedAt)}
            onChange={(e) => setNotify(e.target.checked)}
          />
          {entry?.emailedAt ? "Al gemaild naar volgers" : "Mail naar volgers bij publiceren"}
        </label>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="btnrow">
        {entry?.status === "published" ? (
          <>
            <button type="submit" className="btn primary" disabled={busy}>
              Opslaan
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api(`/helpcenter/changelog/${entry.id}/unpublish`, { method: "POST", body: {} }),
                  "Terug naar concept",
                )
              }
            >
              Terug naar concept
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={busy || !form.title.trim()}
              onClick={() => void publish()}
            >
              Publiceren
            </button>
            <button type="submit" className="btn" disabled={busy}>
              Opslaan als concept
            </button>
          </>
        )}
        <button type="button" className="btn ghost" onClick={onDone}>
          Annuleren
        </button>
        {entry && (
          <button
            type="button"
            className="btn ghost danger"
            disabled={busy}
            onClick={() =>
              void run(() => api(`/helpcenter/changelog/${entry.id}`, { method: "DELETE" }), "Update verwijderd")
            }
          >
            <IconTrash size={18} aria-hidden="true" />
            Verwijderen
          </button>
        )}
      </div>
    </form>
  );
}
